import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from '../../components/common';
import { TabbedInterface } from '../../components/common';
import { StoreInfoCard } from '../../components/store/StoreInfoCard';
import { NotesSection } from '../../components/store/NotesSection';
import { NotesList } from '../../components/store/NotesList';
import { OptimizedHeritageOrderForm } from '../../components/forms/OptimizedHeritageOrderForm';
import { OptimizedLivingstonOrderForm } from '../../components/forms/OptimizedLivingstonOrderForm';
import { OptimizedFlowerOrderForm } from '../../components/forms/OptimizedFlowerOrderForm';
import { OptimizedVegetableOrderForm } from '../../components/forms/OptimizedVegetableOrderForm';
import { StoreOrderSuppliesForm } from '../../components/forms/StoreOrderSuppliesForm';
import { PlanogramOrderForm } from '../../components/forms/PlanogramOrderForm';
import { GlobalOrderProvider, useGlobalOrderData } from '../../hooks/useGlobalOrderData';
import { GlobalSubmitButton } from '../../components/forms/shared/GlobalSubmitButton';
import { GlobalOrderSummary } from '../../components/forms/shared/GlobalOrderSummary';
import { PreviewOrderButton } from '../../components/forms/shared/PreviewOrderButton';
import { CustomerInformationSection, OrderInformationSection } from '../../components/forms/shared/SharedFormSections';
import { useCustomerData } from '../../components/forms/shared/CustomerDataService';
import { OrderPreviewScreen } from '../../components/forms/shared/OrderPreviewScreen';
import { OrderSuppliesPreviewScreen } from '../../components/forms/shared/OrderSuppliesPreviewScreen';
import { HistoricalOrderPreviewScreen } from '../../components/forms/shared/HistoricalOrderPreviewScreen';
import { StoreHistory } from '../../components/common/StoreHistory';
import { ResetOrderButton } from '../../components/forms/shared/ResetOrderButton';
import { 
  PageContainer,
  MainContent,
  ContentSection,
  SectionTitle,
  Grid,
  Heading2,
  Text
} from '../../styles/StyledComponents';
import { ThemeColors, Spacing } from '../../styles/ThemeStyles';
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  deleteDoc,
  // getFirestore,
  Timestamp,
  collection,
  addDoc,
  setDoc,
  query,
  orderBy,
} from 'firebase/firestore';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from 'firebase/storage';
import { db as firestore, storage } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FileStorageService } from '../../services/FileStorageService';
import { UserManagementService } from '../../services/UserManagementService';
import styled from 'styled-components';
import { Store, FileText, Package, Flower, Carrot, ShoppingCart, History, Package2, LayoutGrid } from 'lucide-react';

const STORE_DETAIL_TAB_SLUGS: readonly string[] = ['store-visit', 'store-order', 'store-history', 'order-supplies'];

interface Media {
  url?: string;
  dataUrl?: string;
  type: 'image' | 'document';
  filename?: string;
}

// Note type to match NotesList expectations
type Note = {
  id: string;
  text: string;
  date: string;
  time?: string;
  author: string;
  salesRepName?: string;
  visitType?: 'setup' | 'maintenance' | 'requested' | '';
  competitors?: string[];
  competitorData?: { id: string; name: string; photoUrls?: string[]; stockLevel: number }[];
  media?: Media[];
  documents?: Media[];
  nextVisitDate?: string;
  storeLevel?: number;
  lowStockCategories?: string[];
  requestManagerContact?: boolean;
  replies?: Note[];
  createdAt?: any;
};

interface Store {
  // Match Firestore field names
  CustomerNo: string;
  Address: string;
  StoreName: string;
  City: string;
  StoreNumber: string;
  Prov: string;
  PostalCode: string;
  SortField: string;
  Telephone: string;
  SalesGroup: string;
  EmailAdddress: string; // Note: typo in Firestore field name
  SalespersonNo?: string;
  UDF_Outr_PickupNo?: string;
  notes: Note[];
}

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: ${Spacing.md};
`;

const LoadingText = styled(Text)`
  color: ${ThemeColors.text.secondary};
  margin-top: ${Spacing.sm};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 50vh;
  gap: ${Spacing.lg};
`;

// Desktop-optimized layout for Store Visit tab
const DesktopLayout = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: ${Spacing.xl};
  align-items: start;
  max-width: 100%;
  
  @media (min-width: 1400px) {
    grid-template-columns: 320px 1fr;
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: 280px 1fr;
    gap: ${Spacing.lg};
  }
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.md};
  }
`;

const StoreInfoColumn = styled.div`
  position: sticky;
  top: ${Spacing.md};
  max-height: calc(100vh - ${Spacing.xl});
  overflow-y: auto;
  width: 100%;
  
  @media (max-width: 968px) {
    position: static;
    max-height: none;
  }
`;

const NotesColumn = styled.div`
  min-width: 0; // Prevents grid overflow
  width: 100%;
  max-width: 100%;
`;

const ErrorText = styled(Text)`
  color: ${ThemeColors.error.main};
  font-size: 18px;
  font-weight: 500;
`;

export const StoreDetailsScreenDesktop = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Helper function to get localStorage key for this store
  const getStorageKey = (key: string) => `store_${storeId}_${key}`;
  
  // Load persisted state from localStorage on mount
  const loadPersistedState = () => {
    if (!storeId) return { showPreview: false, activeTab: 'store-visit', selectedHistoricalOrderId: null };
    
    // Check for tab query parameter first (overrides persisted state)
    const tabParam = searchParams.get('tab');
    if (tabParam && STORE_DETAIL_TAB_SLUGS.includes(tabParam)) {
      return {
        showPreview: false,
        activeTab: tabParam,
        selectedHistoricalOrderId: null
      };
    }
    
    try {
      const persistedShowPreview = localStorage.getItem(getStorageKey('showPreview'));
      const persistedActiveTab = localStorage.getItem(getStorageKey('activeTab'));
      const persistedHistoricalOrderId = localStorage.getItem(getStorageKey('selectedHistoricalOrderId'));
      
      return {
        showPreview: persistedShowPreview === 'true',
        activeTab: persistedActiveTab || 'store-visit',
        selectedHistoricalOrderId: persistedHistoricalOrderId || null
      };
    } catch (error) {
      console.error('Error loading persisted state:', error);
      return { showPreview: false, activeTab: 'store-visit', selectedHistoricalOrderId: null };
    }
  };
  
  // Save state to localStorage
  const saveState = (key: string, value: any) => {
    if (!storeId) return;
    try {
      localStorage.setItem(getStorageKey(key), String(value));
    } catch (error) {
      console.error(`Error saving state ${key}:`, error);
    }
  };
  
  // Load initial state from localStorage
  const persistedState = loadPersistedState();
  
  // Preview state - load from localStorage
  const [showPreview, setShowPreview] = useState(persistedState.showPreview);
  
  // Customer and Order Information Component
  const CustomerAndOrderSections = () => {
    const { customerData, loading: customerLoading, error: customerError } = useCustomerData(storeId || '');
    const { orderData, updateOrderData } = useGlobalOrderData();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Sync customer data with global order data when customer data is loaded
    // Use a ref to track what we've already synced to prevent infinite loops
    const lastSyncedCustomerRef = React.useRef<string | null>(null);
    
    React.useEffect(() => {
      if (customerData?.storeName && customerData?.customerNumber) {
        // Create a unique key for this customer data
        const customerKey = `${customerData.storeName}-${customerData.customerNumber}`;
        
        // Only sync if this is a different customer than we've already synced
        if (lastSyncedCustomerRef.current !== customerKey) {
          // Check if orderData is empty or different from customerData
          const needsSync = !orderData.storeName || 
                           orderData.storeName !== customerData.storeName ||
                           orderData.customerNumber !== customerData.customerNumber;
          
          if (needsSync) {
            lastSyncedCustomerRef.current = customerKey;
            // ONLY sync customer info fields, don't overwrite order info fields (poNumber, specialInstructions, etc.)
            const customerInfo = {
              customerNumber: customerData.customerNumber,
              storeName: customerData.storeName,
              address: customerData.address,
              city: customerData.city,
              province: customerData.province,
              postalCode: customerData.postalCode,
              email: customerData.email,
              phoneNumber: customerData.phoneNumber
            };
            updateOrderData(customerInfo);
          }
        }
      }
    }, [customerData?.storeName, customerData?.customerNumber]); // Only depend on customerData fields, not orderData or updateOrderData

    const handleInputChange = (field: string, value: string | boolean) => {
      updateOrderData({ [field]: value });
      // Clear error when user starts typing
      if (errors[field]) {
        setErrors(prev => ({ ...prev, [field]: '' }));
      }
    };

    return (
      <div>
        {/* Order Summary and Preview Button - Moved to top */}
        <div style={{ marginBottom: Spacing.lg }}>
          <GlobalOrderSummary orderType="store-order" />
          <PreviewOrderButton onPreview={handleShowPreview} orderType="store-order" />
        </div>

        {/* Customer Information */}
        <CustomerInformationSection
          data={orderData}
          onChange={handleInputChange}
          errors={errors}
          loading={customerLoading}
          error={customerError}
        />

        {/* Order Information */}
        <OrderInformationSection
          data={orderData}
          onChange={handleInputChange}
          errors={errors}
        />
      </div>
    );
  };
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [store, setStore] = useState<Store | null>(null);
  const [userFullName, setUserFullName] = useState<string>('');
  
  // Historical order preview state
  const [selectedHistoricalOrderId, setSelectedHistoricalOrderId] = useState<string | null>(persistedState.selectedHistoricalOrderId);
  
  // Active tab state for TabbedInterface
  const [activeTab, setActiveTab] = useState<string>(persistedState.activeTab);

  const tabChangeFromUiRef = useRef(false);

  const previewUrlValue = showPreview
    ? activeTab === 'order-supplies'
      ? 'supplies'
      : 'order'
    : null;

  useEffect(() => {
    if (!storeId) return;
    const tabSlug = STORE_DETAIL_TAB_SLUGS.includes(activeTab) ? activeTab : 'store-visit';
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tabSlug);
        if (previewUrlValue) {
          next.set('preview', previewUrlValue);
        } else {
          next.delete('preview');
        }
        return next;
      },
      { replace: true }
    );
  }, [activeTab, previewUrlValue, storeId, setSearchParams]);

  const tabFromUrl = searchParams.get('tab');
  useEffect(() => {
    if (!storeId) return;
    if (!tabFromUrl || !STORE_DETAIL_TAB_SLUGS.includes(tabFromUrl)) {
      return;
    }
    if (tabChangeFromUiRef.current) {
      tabChangeFromUiRef.current = false;
      return;
    }
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
      setShowPreview(false);
      saveState('showPreview', 'false');
      saveState('activeTab', tabFromUrl);
    }
  }, [tabFromUrl, activeTab, storeId]);

  // Effect to save state changes to localStorage
  useEffect(() => {
    if (storeId) {
      saveState('activeTab', activeTab);
    }
  }, [activeTab, storeId]);
  
  useEffect(() => {
    if (storeId) {
      saveState('showPreview', showPreview ? 'true' : 'false');
    }
  }, [showPreview, storeId]);
  
  useEffect(() => {
    if (storeId) {
      saveState('selectedHistoricalOrderId', selectedHistoricalOrderId || '');
    }
  }, [selectedHistoricalOrderId, storeId]);

  // Preview handlers
  const handleShowPreview = () => {
    setShowPreview(true);
    saveState('showPreview', 'true');
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
  };

  const handleBackFromPreview = () => {
    setShowPreview(false);
    saveState('showPreview', 'false');
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
  };

  const handleFinalSubmit = async (email: string) => {
    // This will be handled by the OrderPreviewScreen component
    // using the submitOrderWithEmail function from useGlobalOrderData
  };

  // Historical order handlers - save to localStorage when state changes
  const handleOrderClick = (orderId: string) => {
    setSelectedHistoricalOrderId(orderId);
    saveState('selectedHistoricalOrderId', orderId);
  };

  const handleBackFromHistoricalPreview = () => {
    setSelectedHistoricalOrderId(null);
    saveState('selectedHistoricalOrderId', '');
    // Don't change tab here - let onNavigate handle it
  };


  useEffect(() => {
    if (!storeId) {
      navigate('/dashboard');
      return;
    }
    
    const fetchStoreDetails = async () => {
      try {
        // Using imported firestore instance
        const storeRef = doc(firestore, 'Stores', storeId);
        const storeSnap = await getDoc(storeRef);

        if (storeSnap.exists()) {
          const storeData = storeSnap.data();
          
          // Fetch notes from the subcollection
          const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
          const notesQuery = query(notesCollectionRef, orderBy('createdAt', 'desc'));
          const notesSnapshot = await getDocs(notesQuery);
          
          const notesData: Note[] = [];
          notesSnapshot.forEach((noteDoc) => {
            const noteData = noteDoc.data();
            
            // Ensure competitorData is properly structured
            let competitorData = undefined;
            if (noteData.competitorData && Array.isArray(noteData.competitorData)) {
              competitorData = noteData.competitorData.map((comp: any) => ({
                id: comp.id || '',
                name: comp.name || '',
                photoUrls: Array.isArray(comp.photoUrls) ? comp.photoUrls : (comp.photoUrls ? [comp.photoUrls] : []),
                stockLevel: comp.stockLevel || 0,
                displayPanels: comp.displayPanels !== undefined && comp.displayPanels !== null ? Number(comp.displayPanels) : undefined
              }));
              
              // Debug logging
              if (process.env.NODE_ENV === 'development') {
                console.log(`[StoreDetailsScreen] Loaded competitorData for note ${noteDoc.id}:`, competitorData);
              }
            }
            
            notesData.push({
              id: noteDoc.id,
              text: noteData.text || '',
              date: noteData.date || '',
              time: noteData.time || '',
              author: noteData.author || '',
              salesRepName: noteData.salesRepName || '',
              visitType: noteData.visitType || undefined,
              competitors: noteData.competitors || [],
              competitorData: competitorData,
              media: noteData.media || [],
              documents: noteData.documents || [],
              nextVisitDate: noteData.nextVisitDate || '',
              storeLevel: noteData.storeLevel !== undefined && noteData.storeLevel !== null ? Number(noteData.storeLevel) : undefined,
              lowStockCategories: noteData.lowStockCategories || [],
              requestManagerContact: noteData.requestManagerContact || false,
              replies: noteData.replies || [], // Ensure replies are included
              createdAt: noteData.createdAt
            });
          });
          
          // Use Firestore data directly with our Store interface
          setStore({
            CustomerNo: storeData.CustomerNo || '',
            Address: storeData.Address || '',
            StoreName: storeData.StoreName || '',
            City: storeData.City || '',
            StoreNumber: storeData.StoreNumber || '',
            Prov: storeData.Prov || '',
            PostalCode: storeData.PostalCode || '',
            SortField: storeData.SortField || '',
            Telephone: storeData.Telephone || '',
            SalesGroup: storeData.SalesGroup || '',
            EmailAdddress: storeData.EmailAdddress || storeData.EmailAddress || storeData.email || '',
            SalespersonNo: storeData.SalespersonNo,
            UDF_Outr_PickupNo: storeData.UDF_Outr_PickupNo,
            notes: notesData,
          });
        } else {
          console.error('Store not found');
          alert(t('storeDetails.storeNotFound', 'Error: Store not found'));
          navigate('/dashboard');
        }
      } catch (error) {
        console.error("Error fetching store details:", error);
        alert(t('storeDetails.loadingError', 'Error: Failed to load store details.'));
      } finally {
        setLoading(false);
      }
    };

    fetchStoreDetails();
  }, [storeId, navigate]);

  // Fetch user profile to get firstName and lastName
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.id) return;
      
      try {
        const userProfile = await UserManagementService.getUserById(user.id);
        if (userProfile) {
          const fullName = userProfile.firstName && userProfile.lastName
            ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
            : userProfile.displayName || user?.email || '';
          setUserFullName(fullName);
        } else {
          // Fallback to email if profile not found
          setUserFullName(user?.email || '');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        // Fallback to email if error
        setUserFullName(user?.email || '');
      }
    };

    fetchUserProfile();
  }, [user?.id, user?.email]);

  const handleSubmitNote = async (
    note: string, 
    salesRepName: string,
    selectedCompetitors: string[], 
    attachments: { media: File[], documents: File[] },
    nextVisitDate: string,
    compressionEnabled: boolean = true,
    storeLevel?: number,
    lowStockCategories?: string[],
    requestManagerContact?: boolean,
    competitorData?: { id: string; name: string; photos: File[]; stockLevel: number; displayPanels?: number }[],
    visitType?: 'setup' | 'maintenance' | 'requested' | 'other' | '',
    visitReason?: string,
    onUploadProgress?: (progress: any) => void
  ) => {
    if (!store) return;
    
    setSubmitting(true);
    
    try {
      const fileStorageService = FileStorageService.getInstance();
      const noteId = `note-${Date.now()}`;
      const uploadedBy = user?.email || 'Unknown';

      const uploadResults = await fileStorageService.uploadAllNoteFiles(
        storeId!,
        noteId,
        attachments.media,
        attachments.documents,
        competitorData || [],
        uploadedBy,
        compressionEnabled,
        undefined,
        onUploadProgress
      );

      // Log any upload errors
      if (uploadResults.errors && uploadResults.errors.length > 0) {
        console.warn('⚠️ Some files failed to upload:', uploadResults.errors);
      }

      // Convert upload results to Media and Document objects
      const uploadedMedia: Media[] = uploadResults.media.map(result => ({
        url: result.url,
        type: 'image' as const,
        filename: result.name
      }));

      const uploadedDocuments: Media[] = uploadResults.documents.map(result => ({
        url: result.url,
        type: 'document' as const,
        filename: result.name
      }));

      // Build competitor data with uploaded photo URLs
      let competitorDataWithUrls: { id: string; name: string; photoUrls: string[]; stockLevel: number; displayPanels?: number }[] = [];
      if (competitorData && competitorData.length > 0) {
        for (const competitor of competitorData) {
          const photoUrls = (uploadResults.competitorPhotos[competitor.id] || []).map(r => r.url);
          competitorDataWithUrls.push({
            id: competitor.id,
            name: competitor.name,
            photoUrls: photoUrls,
            stockLevel: competitor.stockLevel,
            ...(competitor.displayPanels !== undefined && competitor.displayPanels > 0 ? { displayPanels: competitor.displayPanels } : {})
          });
        }
      }

      // Get current date and time
      const now = new Date();
      // Use explicit locale to avoid issues on Android browsers (Samsung Galaxy Chrome, etc.)
      const formattedDate = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
      const formattedTime = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      // Create new note object with proper validation
      const newNote: Note = {
        id: noteId,
        text: note || '',
        date: formattedDate,
        time: formattedTime,
        author: user?.email || 'Anonymous',
        salesRepName: salesRepName || '',
        visitType: visitType || undefined,
        competitors: selectedCompetitors || [],
        // Only include competitorData if it has values (Firestore doesn't allow undefined)
        ...(competitorDataWithUrls.length > 0 ? { competitorData: competitorDataWithUrls } : {}),
        media: uploadedMedia || [],
        documents: uploadedDocuments || [],
        nextVisitDate: nextVisitDate || undefined,
        storeLevel: storeLevel !== undefined && storeLevel !== null ? storeLevel : 100, // Default to 100 if not provided
        lowStockCategories: lowStockCategories || [],
        requestManagerContact: requestManagerContact || false,
        visitReason: visitReason || undefined,
        createdAt: Timestamp.now()
      };
      
      // Update the local state immediately to provide instant feedback
      if (store) {
        const updatedStore = {
          ...store,
          notes: [newNote, ...(store.notes || [])]
        };
        setStore(updatedStore);
      }
      
      // Save to Firestore as a subcollection document
      try {
        // Using imported firestore instance
        const storeRef = doc(firestore, 'Stores', storeId!);
        
        // Convert nextVisitDate string to proper format
        let nextVisitTimestamp = null;
        if (nextVisitDate) {
          let daysToAdd = 0;
          if (nextVisitDate === 'Next Week') {
            daysToAdd = 7;
          } else if (nextVisitDate === '+2 Weeks') {
            daysToAdd = 14;
          } else if (nextVisitDate === '+3 Weeks') {
            daysToAdd = 21;
          } else if (nextVisitDate === '+4 Weeks') {
            daysToAdd = 28;
          } else if (nextVisitDate === '+5 Weeks') {
            daysToAdd = 35;
          }
          
          if (daysToAdd > 0) {
            const nextVisit = new Date(Date.now() + daysToAdd * 24 * 60 * 60 * 1000);
            nextVisitTimestamp = Timestamp.fromDate(nextVisit);
          }
        }
        
        // Save note as a document in the notes subcollection
        const notesCollectionRef = collection(firestore, 'Stores', storeId!, 'notes');
        const noteDocRef = doc(notesCollectionRef, noteId);
        
        // Ensure storeLevel is always saved as a number (default to 100 if not provided)
        const noteToSave: any = {
          ...newNote,
          storeLevel: typeof newNote.storeLevel === 'number' ? newNote.storeLevel : 100
        };
        
        // Remove undefined fields (Firestore doesn't support undefined)
        Object.keys(noteToSave).forEach(key => {
          if (noteToSave[key] === undefined) {
            delete noteToSave[key];
          }
        });
        
        console.log('Saving note with storeLevel:', noteToSave.storeLevel);
        await setDoc(noteDocRef, noteToSave);
        
        // Update the nextVisitDate on the store document if provided
        if (nextVisitTimestamp) {
          await updateDoc(storeRef, {
            nextVisitDate: nextVisitTimestamp
          });
        }

        // Update denormalized fields (notesCount, lastVisit, lastVisitedBy) for database-level sorting
        const { StoreService } = await import('../../services/StoreService');
        StoreService.updateStoreDenormalizedFields(storeId!).catch(err => {
          console.warn('Failed to update denormalized fields:', err);
        });
        
        // Trigger notification for note added - notify all managers
        if (user?.id || user?.email) {
          const { NotificationTriggerService } = await import('../../services/NotificationTriggerService');
          NotificationTriggerService.triggerNoteAddedNotification(
            storeId!,
            noteId,
            user?.id || user?.email || 'Unknown',
            store?.StoreName || ''
          ).catch(err => {
            console.error('Failed to trigger note notification:', err);
            // Don't fail the note submission if notification fails
          });
        }
        
        // Success message is handled by NotesSection component
        console.log('Note submitted successfully');
      } catch (firestoreError: any) {
        console.error("Error updating Firestore:", firestoreError);
        // Provide more detailed error message for debugging
        const errorMessage = firestoreError?.message || firestoreError?.toString() || 'Unknown error';
        console.error('Error details:', {
          message: errorMessage,
          stack: firestoreError?.stack,
          code: firestoreError?.code,
          name: firestoreError?.name
        });
        // Re-throw so the caller can handle it
        throw firestoreError;
      }
    } catch (error) {
      console.error("Error submitting note:", error);
      // Re-throw so the caller (NotesSection) can handle it and show error message
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReply = async (
    noteId: string,
    text: string,
    media: File[],
    documents: File[],
    compressionEnabled: boolean = true
  ) => {
    if (!storeId || !store) {
      console.error('Cannot submit reply: storeId or store is missing');
      throw new Error('Store information is missing');
    }
    
    try {
      const fileStorageService = FileStorageService.getInstance();
      const replyId = `reply-${Date.now()}`;
      const uploadedBy = user?.email || 'Unknown';

      // Upload files to Firebase Storage
      const uploadResults = await fileStorageService.uploadStoreNoteAttachments(
        storeId,
        replyId,
        media,
        documents,
        uploadedBy,
        compressionEnabled
      );

      // Convert upload results to Media and Document objects
      const mediaResults: Media[] = uploadResults.media.map(result => ({
        url: result.url,
        type: 'image' as const,
        filename: result.name
      }));

      const documentResults: Media[] = uploadResults.documents.map(result => ({
        url: result.url,
        type: 'document' as const,
        filename: result.name
      }));

      // Create the reply object
      const now = new Date();
      // Use explicit locale to avoid issues on Android browsers (Samsung Galaxy Chrome, etc.)
      const formattedDate = now.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      });
      const formattedTime = now.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
      
      const newReply: Note = {
        id: replyId,
        text,
        author: user?.email || 'Unknown',
        salesRepName: user?.email || 'Unknown',
        date: formattedDate,
        time: formattedTime,
        media: mediaResults,
        documents: documentResults,
        createdAt: Timestamp.now()
      };

      // Update the note document in the subcollection with the reply
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      const noteDocSnap = await getDoc(noteDocRef);
      
      if (!noteDocSnap.exists()) {
        throw new Error('Note not found');
      }

      const noteData = noteDocSnap.data();
      const existingReplies = (noteData.replies || []) as Note[];
      
      // Add the new reply to the existing replies array
      const updatedReplies = [...existingReplies, newReply];

      // Update the note document in the subcollection
      await updateDoc(noteDocRef, {
        replies: updatedReplies
      });

      // Update local state
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
          notes: prevStore.notes.map((note: Note) => {
            if (note.id === noteId) {
              return {
                ...note,
                replies: updatedReplies
              };
            }
            return note;
          })
        };
      });

      // Trigger notification for reply to note
      if (user?.id && noteData.author) {
        const { NotificationTriggerService } = await import('../../services/NotificationTriggerService');
        NotificationTriggerService.triggerNoteReplyNotification(
          storeId,
          noteId,
          user.id,
          noteData.author, // Original note author email
          store?.StoreName || ''
        ).catch(err => {
          console.error('Failed to trigger reply notification:', err);
          // Don't fail the reply submission if notification fails
        });
      }

      console.log('✅ Reply submitted successfully', { noteId, replyId, repliesCount: updatedReplies.length });
    } catch (err) {
      console.error('❌ Error submitting reply:', err);
      throw err;
    }
  };

  // Helper function to refetch notes
  const refetchNotes = async () => {
    if (!storeId) return;
    
    try {
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const notesQuery = query(notesCollectionRef, orderBy('createdAt', 'desc'));
      const notesSnapshot = await getDocs(notesQuery);
      
      const notesData: Note[] = [];
      notesSnapshot.forEach((noteDoc) => {
        const noteData = noteDoc.data();
        
        let competitorData = undefined;
        if (noteData.competitorData && Array.isArray(noteData.competitorData)) {
          competitorData = noteData.competitorData.map((comp: any) => ({
            id: comp.id || '',
            name: comp.name || '',
            photoUrls: Array.isArray(comp.photoUrls) ? comp.photoUrls : (comp.photoUrls ? [comp.photoUrls] : []),
            stockLevel: comp.stockLevel || 0
          }));
        }
        
        notesData.push({
          id: noteDoc.id,
          text: noteData.text || '',
          date: noteData.date || '',
          time: noteData.time || '',
          author: noteData.author || '',
          salesRepName: noteData.salesRepName || '',
          visitType: noteData.visitType || undefined,
          competitors: noteData.competitors || [],
          competitorData: competitorData,
          media: noteData.media || [],
          documents: noteData.documents || [],
          nextVisitDate: noteData.nextVisitDate || '',
          storeLevel: noteData.storeLevel !== undefined && noteData.storeLevel !== null ? Number(noteData.storeLevel) : undefined,
          lowStockCategories: noteData.lowStockCategories || [],
          requestManagerContact: noteData.requestManagerContact || false,
          replies: noteData.replies || [],
          createdAt: noteData.createdAt
        });
      });
      
      return notesData;
    } catch (error) {
      console.error('Error refetching notes:', error);
      return [];
    }
  };

  const handleRefreshNotes = async () => {
    const notesData = await refetchNotes();
    if (notesData) {
      setStore(prevStore => {
        if (!prevStore) return null;
        return { ...prevStore, notes: notesData };
      });
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!storeId || !store) return;
    
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }
    
    try {
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      await deleteDoc(noteDocRef);
      
      const notesData = await refetchNotes();
      
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
          notes: notesData
        };
      });
      
      console.log('✅ Note deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  const handleDeleteReply = async (noteId: string, replyId: string) => {
    if (!storeId || !store) return;
    
    if (!window.confirm('Are you sure you want to delete this reply? This action cannot be undone.')) {
      return;
    }
    
    try {
      const notesCollectionRef = collection(firestore, 'Stores', storeId, 'notes');
      const noteDocRef = doc(notesCollectionRef, noteId);
      const noteDocSnap = await getDoc(noteDocRef);
      
      if (!noteDocSnap.exists()) {
        throw new Error('Note not found');
      }
      
      const noteData = noteDocSnap.data();
      const existingReplies = (noteData.replies || []) as Note[];
      
      // Remove the reply from the array
      const updatedReplies = existingReplies.filter(reply => reply.id !== replyId);
      
      // Update the note document
      await updateDoc(noteDocRef, {
        replies: updatedReplies
      });
      
      // Reload notes
      const notesData = await refetchNotes();
      
      // Update local state
      setStore(prevStore => {
        if (!prevStore) return null;
        return {
          ...prevStore,
          notes: notesData
        };
      });
      
      console.log('✅ Reply deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting reply:', error);
      alert('Failed to delete reply. Please try again.');
    }
  };

  if (loading) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <LoadingContainer>
            <ActivityIndicator size="large" color={ThemeColors.primary.main} />
            <LoadingText>Loading store details...</LoadingText>
          </LoadingContainer>
        </MainContent>
      </PageContainer>
    );
  }

  if (!store) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <ErrorContainer>
            <ErrorText>Store not found</ErrorText>
          </ErrorContainer>
        </MainContent>
      </PageContainer>
    );
  }

  // Show historical order preview if an order is selected
  if (selectedHistoricalOrderId && storeId) {
    return (
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <ContentSection>
          <HistoricalOrderPreviewScreen
            storeId={storeId}
            orderId={selectedHistoricalOrderId}
            onBack={handleBackFromHistoricalPreview}
            storeName={store.StoreName}
            onNavigate={(tab) => {
              // Handle navigation between tabs
              setSelectedHistoricalOrderId(null);
              saveState('selectedHistoricalOrderId', '');
              if (tab === 'store-history') {
                setActiveTab('store-history');
                saveState('activeTab', 'store-history');
              } else if (tab === 'store-visit') {
                setActiveTab('store-visit');
                saveState('activeTab', 'store-visit');
              } else if (tab === 'store-order') {
                setActiveTab('store-order');
                saveState('activeTab', 'store-order');
              }
            }}
          />
          </ContentSection>
        </MainContent>
      </PageContainer>
    );
  }

  // Show preview screen if preview is active
  if (showPreview) {
    return (
      <GlobalOrderProvider storeId={storeId || ''}>
        <PageContainer isMenuCollapsed={false}>
          <MainContent>
            <ContentSection>
          {activeTab === 'order-supplies' ? (
            <OrderSuppliesPreviewScreen 
              onBack={handleBackFromPreview}
              onFinalSubmit={handleFinalSubmit}
              storeId={storeId}
              storeName={store.StoreName}
              onNavigate={(tab) => {
                if (tab === 'store-visit') {
                  setShowPreview(false);
                } else if (tab === 'store-history') {
                  setShowPreview(false);
                }
              }}
            />
          ) : (
            <OrderPreviewScreen 
              onBack={handleBackFromPreview}
              onFinalSubmit={handleFinalSubmit}
              storeId={storeId}
              storeName={store.StoreName}
              orderType="store-order"
              onNavigate={(tab) => {
                if (tab === 'store-visit') {
                  setShowPreview(false);
                } else if (tab === 'store-history') {
                  setShowPreview(false);
                }
              }}
            />
          )}
            </ContentSection>
          </MainContent>
        </PageContainer>
      </GlobalOrderProvider>
    );
  }

  return (
    <GlobalOrderProvider storeId={storeId || ''}>
      <PageContainer isMenuCollapsed={false}>
        <MainContent>
          <ContentSection>
            <Heading2>{store.StoreName}</Heading2>
            
            <TabbedInterface
              variant="desktop"
              middleSection={<CustomerAndOrderSections />}
              showMiddleSectionForTabs={['store-order']}
              defaultActiveTab="store-visit"
              activeTab={activeTab}
              onTabChange={(tab) => {
                tabChangeFromUiRef.current = true;
                setActiveTab(tab);
                saveState('activeTab', tab);
                setShowPreview(false);
                saveState('showPreview', 'false');
              }}
              tabs={[
                {
                  id: 'store-visit',
                  label: t('navigation.storeVisit', 'Store Visit'),
                  icon: Store,
                  content: (
                    <div>
                      <DesktopLayout>
                        {/* Left side - Store Info (30% width) */}
                        <StoreInfoColumn>
                          <StoreInfoCard 
                            customerNumber={store.CustomerNo}
                            address={store.Address}
                            storeName={store.StoreName}
                            cityTown={store.City}
                            storeNumber={store.StoreNumber}
                            province={store.Prov}
                            postalCode={store.PostalCode}
                            sortField={store.SortField}
                            phoneNumber={store.Telephone}
                            salesGroup={store.SalesGroup}
                            email={store.EmailAdddress}
                          />
                        </StoreInfoColumn>
                        
                        {/* Right side - Notes (70% width) */}
                        <NotesColumn>
                          <NotesSection 
                            onSubmitNote={(note, salesRepName, competitors, attachments, nextVisitDate, compressionEnabled = true, storeLevel, lowStockCategories, requestManagerContact, competitorData, visitType, visitReason, onUploadProgress) => {
                              return handleSubmitNote(
                                note,
                                salesRepName,
                                competitors,
                                attachments,
                                nextVisitDate,
                                compressionEnabled,
                                storeLevel,
                                lowStockCategories,
                                requestManagerContact,
                                competitorData,
                                visitType,
                                visitReason,
                                onUploadProgress
                              );
                            }}
                            defaultSalesRepName={userFullName || user?.email || ''}
                            storeId={storeId}
                          />
                        </NotesColumn>
                      </DesktopLayout>
                      
                      {/* Notes List - Only visible in Store Visit tab */}
                      <ContentSection style={{ marginTop: Spacing.md }}>
                        <NotesList 
                          notes={store.notes || []}
                          onViewMediaFullScreen={(media) => {
                            // Handle media view in browser
                            const imageUrl = media.dataUrl || media.url;
                            if (imageUrl) {
                              window.open(imageUrl, '_blank');
                            }
                          }}
                          onSubmitReply={handleSubmitReply}
                          onDeleteNote={handleDeleteNote}
                          onDeleteReply={handleDeleteReply}
                          onRefreshNotes={handleRefreshNotes}
                          currentUserEmail={user?.email}
                        />
                      </ContentSection>
                    </div>
                  )
                },
                {
                  id: 'store-order',
                  label: 'Store Order',
                  icon: ShoppingCart,
                  subTabs: [
                    {
                      id: 'vegetable-order',
                      label: 'Vegetable Order',
                      icon: Carrot,
                      content: <OptimizedVegetableOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'flower-order',
                      label: 'Flower Order',
                      icon: Flower,
                      content: <OptimizedFlowerOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'heritage-order',
                      label: 'Heritage/Seedtape/Organic',
                      icon: FileText,
                      content: <OptimizedHeritageOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'livingston-order',
                      label: 'Livingston Order',
                      icon: Package,
                      content: <OptimizedLivingstonOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    },
                    {
                      id: 'planogram-order',
                      label: 'Planogram Order',
                      icon: LayoutGrid,
                      content: <PlanogramOrderForm storeId={storeId} onShowPreview={handleShowPreview} />
                    }
                  ]
                },
                {
                  id: 'store-history',
                  label: 'Order History',
                  icon: History,
                  content: (
                    <StoreHistory 
                      storeId={storeId || ''} 
                      storeName={store.StoreName}
                      onOrderClick={handleOrderClick}
                    />
                  )
                },
                {
                  id: 'order-supplies',
                  label: 'Order Supplies',
                  icon: Package2,
                  content: (
                    <div>
                      <div style={{ marginBottom: Spacing.xl }}>
                        <Text style={{ color: ThemeColors.text.secondary, marginBottom: Spacing.lg }}>
                          Order promotional materials and seed displays for this store
                        </Text>
                      </div>
                      <StoreOrderSuppliesForm storeId={storeId} onShowPreview={handleShowPreview} />
                    </div>
                  )
                }
              ]}
            />
          </ContentSection>
        </MainContent>
      </PageContainer>
    </GlobalOrderProvider>
  );
};